import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase/client'
import { isFUnitCategoryCode } from '@/utils/lxBxH'
import { STMIDBEquipmentService } from '@/services/api/stmidbEquipmentService'

/**
 * Normalise a stored prod_acc_to / reference value into tokens.
 *
 * Accepts JSON arrays, comma-separated codes ("EN 818-4, EN 13414-1"),
 * and legacy UUID lists. Does not decide codes vs ids — callers do.
 */
export const normalizeReferenceIds = (raw: unknown): string[] => {
  const out: string[] = []

  const visit = (value: unknown, depth: number): void => {
    if (value == null || depth > 6) return

    if (Array.isArray(value)) {
      value.forEach(item => visit(item, depth + 1))
      return
    }

    if (typeof value !== 'string') return

    const s = value.trim()
    if (!s) return

    // Unwrap JSON-encoded arrays or quoted strings (handles double-encoding).
    if (s.startsWith('[') || s.startsWith('"')) {
      try {
        visit(JSON.parse(s), depth + 1)
        return
      } catch {
        // Single-quoted or otherwise invalid JSON arrays, e.g. ['EN-13889'].
        if (s.startsWith('[') && s.endsWith(']')) {
          const inner = s.slice(1, -1)
          if (!inner.trim()) return
          inner.split(',').forEach(part => visit(part.trim(), depth + 1))
          return
        }
      }
    }

    // Comma-separated list of ids — but not a leftover JSON array token.
    if (s.includes(',') && !s.startsWith('[')) {
      s.split(',').forEach(part => visit(part, depth + 1))
      return
    }

    const cleaned = s.replace(/^["']+|["']+$/g, '').trim()
    if (cleaned && !cleaned.startsWith('[')) out.push(cleaned)
  }

  visit(raw, 0)
  return Array.from(new Set(out))
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const isUuid = (value: string): boolean => UUID_RE.test(value)

/**
 * Persist prod_acc_to as comma-separated standards_v2.code values
 * (e.g. "EN 818-4, EN 13414-1"). UUIDs are resolved to codes when possible
 * and otherwise dropped — never write catalog IDs into value_text.
 */
export const formatProdAccToValueText = (
  raw: unknown,
  resolveUuidToCode?: (id: string) => string | undefined,
): string => {
  const codes: string[] = []
  for (const token of normalizeReferenceIds(raw)) {
    const t = token.trim()
    if (!t) continue
    if (isUuid(t)) {
      const code = resolveUuidToCode?.(t)?.trim()
      if (code && !isUuid(code)) codes.push(code)
      continue
    }
    codes.push(t)
  }
  return Array.from(new Set(codes)).join(', ')
}

// Types based on the canonical template system
export interface SpecificationTemplate {
  id: string
  spec_key: string
  label_en: string
  label_no: string
  description_en?: string
  description_no?: string
  data_type: string
  unit_category?: string
  default_unit_id?: string
  min_value?: number
  max_value?: number
  validation_pattern?: string
  enum_options?: string[]
  reference_table?: string
  reference_type?: string
  group_name?: string
  group_order: number
  display_order: number
  is_visible: boolean
  default_value?: string
  is_active: boolean
  // Category context
  category_id?: string
  category_code?: string
  category_is_required?: boolean
  category_display_order?: number
}

export interface EquipmentSpecification {
  id: string
  equipment_id: string
  template_id: string
  spec_key: string
  value_text: string | null
  value_numeric: number | null
  value_integer: number | null
  value_date: string | null
  value_boolean: boolean | null
  value_reference_id: string | null
  unit_id: string | null
  unit?: {
    symbol: string
    name_en: string
  }
  // Resolved reference values for display
  manufacturer?: { name: string }
  standard?: { code: string; title: string }
  standards?: Array<{ code: string; title: string }>
}

export interface SpecWithTemplate {
  template: SpecificationTemplate
  spec: EquipmentSpecification | null
  displayValue: string
  rawValue: any
}

interface UseEquipmentSpecificationsOptions {
  equipmentId: string
  categoryId: string | null
  /** 'stmidb' for SLB equipment: specs live in external_equipment_specifications. */
  source?: 'stmidb'
}

export const loadExternalSpecs = async (equipmentId: string): Promise<EquipmentSpecification[]> => {
  const rows = await STMIDBEquipmentService.getExternalEquipmentSpecs(equipmentId)
  const unitIds = Array.from(new Set(rows.map(r => r.unit_id).filter((u): u is string => !!u)))
  const units: Record<string, { symbol: string; name_en: string }> = {}
  if (unitIds.length > 0) {
    const { data } = await supabase.from('units_v2').select('id, symbol, name_en').in('id', unitIds)
    ;(data || []).forEach(u => { units[u.id] = { symbol: u.symbol, name_en: u.name_en } })
  }
  return rows.map(r => ({
    id: r.id,
    equipment_id: equipmentId,
    template_id: r.template_id || '',
    spec_key: r.spec_key,
    value_text: r.value_text,
    value_numeric: r.value_numeric,
    value_integer: r.value_integer,
    value_date: r.value_date,
    value_boolean: r.value_boolean,
    value_reference_id: r.value_reference_id,
    unit_id: r.unit_id,
    unit: r.unit_id ? units[r.unit_id] : undefined,
  }))
}

const saveExternalSpec = async (
  equipmentId: string,
  specData: Pick<EquipmentSpecification, 'spec_key' | 'template_id' | 'value_text' | 'value_numeric' | 'value_integer' | 'value_date' | 'value_boolean' | 'value_reference_id' | 'unit_id'>,
): Promise<EquipmentSpecification> => {
  const id = await STMIDBEquipmentService.saveExternalEquipmentSpec({
    externalEquipmentId: equipmentId,
    specKey: specData.spec_key,
    templateId: specData.template_id,
    valueText: specData.value_text,
    valueNumeric: specData.value_numeric,
    valueInteger: specData.value_integer,
    valueDate: specData.value_date,
    valueBoolean: specData.value_boolean,
    valueReferenceId: specData.value_reference_id,
    unitId: specData.unit_id,
  })
  return { ...specData, id: id || `ext-${specData.spec_key}`, equipment_id: equipmentId }
}

/** F1–F5 units: drop WLL and ensure the dimensions (LxBxH) template is present. */
export const applyFUnitSpecTemplates = async <T extends { spec_key: string }>(
  templates: T[],
  categoryCode?: string | null,
): Promise<T[]> => {
  if (!isFUnitCategoryCode(categoryCode)) return templates

  const withoutWll = templates.filter(t => t.spec_key !== 'wll')
  if (withoutWll.some(t => t.spec_key === 'dimensions')) return withoutWll

  const { data } = await supabase
    .from('canonical_specification_templates')
    .select('*')
    .eq('spec_key', 'dimensions')
    .eq('is_active', true)
    .maybeSingle()

  if (!data) return withoutWll

  return [
    {
      ...data,
      is_visible: true,
      category_is_required: false,
      category_display_order: 0,
    } as T,
    ...withoutWll,
  ]
}

export const useEquipmentSpecifications = ({ equipmentId, categoryId, source }: UseEquipmentSpecificationsOptions) => {
  const isExternal = source === 'stmidb'
  const [templates, setTemplates] = useState<SpecificationTemplate[]>([])
  const [specs, setSpecs] = useState<Record<string, EquipmentSpecification>>({})
  const [specsWithTemplates, setSpecsWithTemplates] = useState<SpecWithTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Reference data caches
  const [manufacturers, setManufacturers] = useState<Record<string, { name: string }>>({})
  const [standards, setStandards] = useState<Record<string, { code: string; title: string }>>({})
  
  // Cache the categoryId to avoid refetching templates unnecessarily
  const lastCategoryIdRef = useRef<string | null>(null)

  // Load templates for category (uses the view for efficiency)
  const loadTemplates = useCallback(async (catId: string) => {
    try {
      const { data: category } = await supabase
        .from('categories_v2')
        .select('code')
        .eq('id', catId)
        .maybeSingle()
      const categoryCode = category?.code as string | undefined

      // Try the view first (recommended approach)
      const { data, error: viewError } = await supabase
        .from('specification_templates_with_categories')
        .select('*')
        .eq('category_id', catId)
        .eq('is_visible', true)
        .order('display_order', { ascending: true })

      if (!viewError && data && data.length > 0) {
        const adjusted = await applyFUnitSpecTemplates(data, categoryCode)
        setTemplates(adjusted)
        return adjusted
      }

      // Fallback: manual join if view doesn't exist or is empty
      const { data: mappings, error: mappingError } = await supabase
        .from('category_specification_templates_v2')
        .select(`
          *,
          template:canonical_specification_templates(*)
        `)
        .eq('category_id', catId)
        .eq('is_visible', true)
        .order('display_order', { ascending: true })

      if (mappingError) {
        // Final fallback: use specification_templates_v2 (legacy)
        const { data: legacyData } = await supabase
          .from('specification_templates_v2')
          .select('*')
          .eq('is_active', true)
          .order('display_order', { ascending: true })

        if (legacyData) {
          const adjusted = await applyFUnitSpecTemplates(legacyData, categoryCode)
          setTemplates(adjusted)
          return adjusted
        }
        throw mappingError
      }

      const flatTemplates = mappings?.map(m => ({
        ...m.template,
        category_id: m.category_id,
        category_is_required: m.is_required,
        category_display_order: m.display_order
      })) || []

      const adjusted = await applyFUnitSpecTemplates(flatTemplates, categoryCode)
      setTemplates(adjusted)
      return adjusted
    } catch (err) {
      console.error('Failed to load templates:', err)
      return []
    }
  }, [])

  // Load specifications for equipment
  const loadSpecs = useCallback(async () => {
    try {
      if (isExternal) {
        const indexed = Object.fromEntries(
          (await loadExternalSpecs(equipmentId)).map(spec => [spec.spec_key, spec]),
        )
        setSpecs(indexed)
        return indexed
      }

      const { data, error: specsError } = await supabase
        .from('equipment_specifications_v2')
        .select(`
          *,
          unit:units_v2!equipment_specifications_v2_unit_id_fkey(symbol, name_en)
        `)
        .eq('equipment_id', equipmentId)

      if (specsError) throw specsError

      // Index by spec_key
      const indexed = (data || []).reduce((acc, spec) => {
        acc[spec.spec_key] = spec
        return acc
      }, {} as Record<string, EquipmentSpecification>)

      setSpecs(indexed)
      return indexed
    } catch (err) {
      console.error('Failed to load specifications:', err)
      return {}
    }
  }, [equipmentId, isExternal])

  // Load reference data (manufacturers, standards)
  const loadReferenceData = useCallback(async (specsList: EquipmentSpecification[]) => {
    const manufacturerIds = new Set<string>()
    const standardIds = new Set<string>()
    const standardCodes = new Set<string>()

    specsList.forEach(spec => {
      const refId = spec.value_reference_id || spec.value_text
      if (!refId) return

      if (spec.spec_key === 'manufacturer') {
        if (spec.value_reference_id) manufacturerIds.add(spec.value_reference_id)
        else if (spec.value_text && spec.value_text.length === 36) manufacturerIds.add(spec.value_text)
      }
      
      if (spec.spec_key === 'prod_acc_to') {
        normalizeReferenceIds(spec.value_text || spec.value_reference_id)
          .forEach(token => {
            if (isUuid(token)) standardIds.add(token)
            else standardCodes.add(token)
          })
      }
    })

    // Fetch manufacturers
    if (manufacturerIds.size > 0) {
      const { data: mfgData } = await supabase
        .from('manufacturers_v2')
        .select('id, name')
        .in('id', Array.from(manufacturerIds))

      if (mfgData) {
        const mfgMap = Object.fromEntries(mfgData.map(m => [m.id, { name: m.name }]))
        setManufacturers(prev => ({ ...prev, ...mfgMap }))
      }
    }

    // Fetch standards. prod_acc_to is sometimes stored as UUIDs, sometimes as
    // codes (e.g. "EN-13889"); only UUIDs are valid for id=in.(...).
    if (standardIds.size > 0 || standardCodes.size > 0) {
      const stdMap: Record<string, { code: string; title: string }> = {}

      const indexRows = (rows: Array<{ id: string; code: string; title: string }> | null) => {
        ;(rows || []).forEach(s => {
          stdMap[s.id] = { code: s.code, title: s.title }
          if (s.code) stdMap[s.code] = { code: s.code, title: s.title }
        })
      }

      if (standardIds.size > 0) {
        const { data: stdData } = await supabase
          .from('standards_v2')
          .select('id, code, title')
          .in('id', Array.from(standardIds))
        indexRows(stdData)
      }

      if (standardCodes.size > 0) {
        const { data: stdByCode } = await supabase
          .from('standards_v2')
          .select('id, code, title')
          .in('code', Array.from(standardCodes))
        indexRows(stdByCode)
        standardCodes.forEach(code => {
          if (!stdMap[code]) stdMap[code] = { code, title: code }
        })
      }

      setStandards(prev => ({ ...prev, ...stdMap }))
    }
  }, [])

  // Get display value for a spec
  const getDisplayValue = useCallback((spec: EquipmentSpecification | null, template: SpecificationTemplate): string => {
    if (!spec) return template.default_value || ''

    // Handle references
    if (spec.spec_key === 'manufacturer' || spec.spec_key === 'manufacturer_id' || spec.spec_key === 'produsent') {
      const refId = spec.value_reference_id || spec.value_text
      if (refId && manufacturers[refId]) {
        return manufacturers[refId].name
      }
    }

    if (spec.spec_key === 'prod_acc_to' || template.spec_key === 'prod_acc_to') {
      const tokens = normalizeReferenceIds(spec.value_text || spec.value_reference_id)
      if (tokens.length === 0) return ''
      const resolved = tokens
        .map(token => standards[token]?.code || (!isUuid(token) ? token : null))
        .filter(Boolean)
      return resolved.join(', ')
    }

    // Handle by data type
    switch (template.data_type) {
      case 'text':
      case 'enum':
        return spec.value_text || ''
      case 'numeric':
      case 'decimal':
      case 'angle':
      case 'currency':
      case 'percentage':
        if (spec.value_numeric !== null && spec.value_numeric !== undefined) {
          const unit = spec.unit?.symbol || ''
          return unit ? `${spec.value_numeric}${unit}` : spec.value_numeric.toString()
        }
        return spec.value_text || ''
      case 'integer':
      case 'year':
        if (spec.value_integer !== null && spec.value_integer !== undefined) {
          return spec.value_integer.toString()
        }
        return spec.value_text || ''
      case 'date':
        return spec.value_date || ''
      case 'boolean':
        return spec.value_boolean !== null ? (spec.value_boolean ? 'Ja' : 'Nei') : ''
      case 'reference':
        return spec.value_reference_id || ''
      default:
        return spec.value_text || ''
    }
  }, [manufacturers, standards])

  // Get raw value for editing
  const getRawValue = useCallback((spec: EquipmentSpecification | null, template: SpecificationTemplate): any => {
    if (!spec) return template.default_value || ''

    // Hand the editor an array of standard codes (never catalog UUIDs).
    if (spec.spec_key === 'prod_acc_to') {
      return normalizeReferenceIds(spec.value_text || spec.value_reference_id)
        .map(token => (isUuid(token) ? standards[token]?.code : token) || null)
        .filter(Boolean)
    }

    if (spec.spec_key === 'manufacturer' || spec.spec_key === 'manufacturer_id' || spec.spec_key === 'produsent') {
      return spec.value_reference_id || spec.value_text || ''
    }

    switch (template.data_type) {
      case 'text':
      case 'enum':
        return spec.value_text || ''
      case 'numeric':
      case 'decimal':
      case 'angle':
      case 'currency':
      case 'percentage':
        if (spec.value_numeric !== null && spec.value_numeric !== undefined) {
          return spec.value_numeric.toString()
        }
        return spec.value_text || ''
      case 'integer':
      case 'year':
        if (spec.value_integer !== null && spec.value_integer !== undefined) {
          return spec.value_integer.toString()
        }
        return spec.value_text || ''
      case 'date':
        return spec.value_date || ''
      case 'boolean':
        return spec.value_boolean || false
      case 'reference':
        return spec.value_reference_id || spec.value_text || ''
      default:
        return spec.value_text || ''
    }
  }, [standards])

  // Build spec data for upsert
  const buildSpecData = useCallback((
    template: SpecificationTemplate,
    value: any,
    existingSpec: EquipmentSpecification | null
  ) => {
    // Always null ALL value columns first (required by one_value_check constraint)
    const base = {
      equipment_id: equipmentId,
      template_id: template.id,
      spec_key: template.spec_key,
      validation_status: 'pending',
      value_text: null as string | null,
      value_numeric: null as number | null,
      value_integer: null as number | null,
      value_date: null as string | null,
      value_boolean: null as boolean | null,
      value_reference_id: null as string | null,
      unit_id: template.default_unit_id || null
    }

    // If we have existing spec ID, include it for update
    if (existingSpec?.id) {
      (base as any).id = existingSpec.id
    }

    // Handle empty values
    if (value === '' || value === null || value === undefined) {
      return base
    }

    // prod_acc_to: comma-separated standards_v2.code only. Never persist UUIDs.
    if (template.spec_key === 'prod_acc_to') {
      const text = formatProdAccToValueText(value, id => standards[id]?.code)
      if (!text) return base
      return { ...base, value_text: text }
    }

    // Handle manufacturer (always reference type)
    if (template.spec_key === 'manufacturer' || template.spec_key === 'manufacturer_id' || template.spec_key === 'produsent') {
      return { ...base, value_reference_id: value }
    }

    // Set the appropriate column based on data type
    switch (template.data_type) {
      case 'text':
      case 'enum':
        return { ...base, value_text: value }
      case 'numeric':
      case 'decimal':
      case 'angle':
      case 'currency':
      case 'percentage':
        const numericValue = String(value).replace(',', '.')
        return { ...base, value_numeric: parseFloat(numericValue) }
      case 'integer':
      case 'year':
        return { ...base, value_integer: parseInt(value, 10) }
      case 'date':
        return { ...base, value_date: value }
      case 'boolean':
        return { ...base, value_boolean: value === true || value === 'true' }
      case 'reference':
        return { ...base, value_reference_id: value }
      default:
        return { ...base, value_text: String(value) }
    }
  }, [equipmentId, standards])

  // Save a single specification (with optimistic update)
  const saveSpecification = useCallback(async (
    template: SpecificationTemplate,
    value: any
  ): Promise<boolean> => {
    setSaving(true)
    setError(null)

    const existingSpec = specs[template.spec_key]
    const isEmpty =
      value === '' || value === null || value === undefined ||
      (Array.isArray(value) && value.length === 0)

    // Clearing a field: delete the row so one_value_check constraint is not violated
    if (isEmpty && existingSpec?.id) {
      // Optimistic: remove from local state
      setSpecs(prev => {
        const next = { ...prev }
        delete next[template.spec_key]
        return next
      })
      try {
        if (isExternal) {
          const deleted = await STMIDBEquipmentService.deleteExternalEquipmentSpec(equipmentId, template.spec_key)
          if (!deleted) throw new Error('Failed to clear')
          return true
        }

        const { error: deleteError } = await supabase
          .from('equipment_specifications_v2')
          .delete()
          .eq('id', existingSpec.id)

        if (deleteError) throw deleteError
        return true
      } catch (err) {
        console.error('Failed to clear specification:', err)
        setError(err instanceof Error ? err.message : 'Failed to clear')
        // Rollback
        setSpecs(prev => ({ ...prev, [template.spec_key]: existingSpec }))
        return false
      } finally {
        setSaving(false)
      }
    }

    // Nothing to do if value is empty and no row exists
    if (isEmpty) {
      setSaving(false)
      return true
    }

    const specData = buildSpecData(template, value, existingSpec)

    // Optimistic update
    const optimisticSpec: EquipmentSpecification = {
      ...specData,
      id: existingSpec?.id || 'temp-' + Date.now(),
      unit: existingSpec?.unit
    } as EquipmentSpecification

    setSpecs(prev => ({ ...prev, [template.spec_key]: optimisticSpec }))

    try {
      if (isExternal) {
        const saved = await saveExternalSpec(equipmentId, specData)
        setSpecs(prev => ({ ...prev, [template.spec_key]: { ...saved, unit: existingSpec?.unit } }))
        await loadReferenceData([saved])
        return true
      }

      const { data, error: upsertError } = await supabase
        .from('equipment_specifications_v2')
        .upsert(specData, {
          onConflict: 'equipment_id,spec_key',
          ignoreDuplicates: false
        })
        .select()
        .single()

      if (upsertError) throw upsertError

      // Update with real data
      setSpecs(prev => ({ ...prev, [template.spec_key]: data }))
      await loadReferenceData([data])
      return true
    } catch (err) {
      console.error('Failed to save specification:', err)
      setError(err instanceof Error ? err.message : 'Failed to save')
      // Rollback optimistic update
      if (existingSpec) {
        setSpecs(prev => ({ ...prev, [template.spec_key]: existingSpec }))
      } else {
        setSpecs(prev => {
          const newSpecs = { ...prev }
          delete newSpecs[template.spec_key]
          return newSpecs
        })
      }
      return false
    } finally {
      setSaving(false)
    }
  }, [specs, buildSpecData, loadReferenceData, isExternal, equipmentId])

  // Save multiple specifications at once (batch)
  const saveSpecifications = useCallback(async (
    updates: Array<{ template: SpecificationTemplate; value: any }>
  ): Promise<boolean> => {
    setSaving(true)
    setError(null)

    // Filter out unchanged values
    const changedUpdates = updates.filter(({ template, value }) => {
      const existingSpec = specs[template.spec_key]
      const currentRawValue = existingSpec ? getRawValue(existingSpec, template) : ''
      return JSON.stringify(value) !== JSON.stringify(currentRawValue)
    })

    if (changedUpdates.length === 0) {
      setSaving(false)
      return true
    }

    // Build all spec data
    const specsToSave = changedUpdates.map(({ template, value }) => {
      const existingSpec = specs[template.spec_key]
      return buildSpecData(template, value, existingSpec)
    })

    // Optimistic update
    const optimisticSpecs = { ...specs }
    changedUpdates.forEach(({ template, value }) => {
      const existingSpec = specs[template.spec_key]
      optimisticSpecs[template.spec_key] = {
        ...buildSpecData(template, value, existingSpec),
        id: existingSpec?.id || 'temp-' + Date.now(),
        unit: existingSpec?.unit
      } as EquipmentSpecification
    })
    setSpecs(optimisticSpecs)

    try {
      if (isExternal) {
        const saved = await Promise.all(specsToSave.map(spec => saveExternalSpec(equipmentId, spec)))
        const updatedSpecs = { ...specs }
        saved.forEach(spec => { updatedSpecs[spec.spec_key] = { ...spec, unit: specs[spec.spec_key]?.unit } })
        setSpecs(updatedSpecs)
        return true
      }

      // Batch upsert
      const { data, error: upsertError } = await supabase
        .from('equipment_specifications_v2')
        .upsert(specsToSave, {
          onConflict: 'equipment_id,spec_key',
          ignoreDuplicates: false
        })
        .select()

      if (upsertError) throw upsertError

      // Update with real data
      if (data) {
        const updatedSpecs = { ...specs }
        data.forEach(spec => {
          updatedSpecs[spec.spec_key] = spec
        })
        setSpecs(updatedSpecs)
      }
      return true
    } catch (err) {
      console.error('Failed to save specifications:', err)
      setError(err instanceof Error ? err.message : 'Failed to save')
      // Rollback
      setSpecs(specs)
      return false
    } finally {
      setSaving(false)
    }
  }, [specs, buildSpecData, getRawValue, isExternal, equipmentId])

  // Combine templates with specs for display
  useEffect(() => {
    const combined = templates.map(template => ({
      template,
      spec: specs[template.spec_key] || null,
      displayValue: getDisplayValue(specs[template.spec_key] || null, template),
      rawValue: getRawValue(specs[template.spec_key] || null, template)
    }))
    setSpecsWithTemplates(combined)
  }, [templates, specs, manufacturers, standards, getDisplayValue, getRawValue])

  // Initial load
  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setError(null)

      try {
        // Load templates (only if category changed)
        if (categoryId && categoryId !== lastCategoryIdRef.current) {
          await loadTemplates(categoryId)
          lastCategoryIdRef.current = categoryId
        }

        // Always load specs for the equipment
        const loadedSpecs = await loadSpecs()
        
        // Load reference data for resolved display
        await loadReferenceData(Object.values(loadedSpecs))
      } catch (err) {
        console.error('Failed to load equipment specifications:', err)
        setError(err instanceof Error ? err.message : 'Failed to load')
      } finally {
        setLoading(false)
      }
    }

    if (equipmentId) {
      load()
    }
  }, [equipmentId, categoryId, loadTemplates, loadSpecs, loadReferenceData])

  // Refresh function for manual reload
  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const loadedSpecs = await loadSpecs()
      await loadReferenceData(Object.values(loadedSpecs))
    } finally {
      setLoading(false)
    }
  }, [loadSpecs, loadReferenceData])

  return {
    templates,
    specs,
    specsWithTemplates,
    loading,
    saving,
    error,
    saveSpecification,
    saveSpecifications,
    refresh,
    manufacturers,
    standards
  }
}

